// The rules of Arellix, shown in the game under "Rules" (on the main menu, in the 1v1 and 2v2 menus and at the table).
// Edit this file and refresh the page: the game reads it as it starts.
//
// How it is written:
//   = Chapter        a group of sections in the list on the left
//   ## Section       one entry in that list
//   ### Heading      a heading inside a section
//   - point          a bullet point;  -- point  a point under the one above
//   **bold**         bold words
//   A line on its own is a paragraph; a blank line ends it. Lines starting with // are notes nobody sees.

= How to Play

## Turn Structure

There are 4 phases during a player's turn:

### Overdraw Phase
- Effects that begin before your Overdraw Phase begin now (such as Bleed damage).
- Untap all cards tapped during your previous turn.
- This Phase marks the start of your turn.
- You gain 2 Gerzite during this Phase (3 during Overtime, 4 during Double Overtime).
- You may spend 1 Gerzite to Overdraw a card (any amount, up to 10 cards in your hand) if you wish during this Phase.
- You may discard any cards from your hand into your discard pile if you wish during this Phase (drag them onto your discard pile). You gain nothing for them except more room in your hand. You can't discard cards from your hand this way during any other Phase.
### Summon Phase
- Effects that begin before your Summon Phase begin now.
- Units summoned during your previous turn are cured of their Entrenched (unless they have Entrenched for more than 1 turn).
- You may choose to summon any card you wish, as long as you can afford it.
- Entrance abilities or mechanics that specify occurring when played (typically always seen in Spells) happen during this Phase.
- You may choose to pass this Phase if you wish.
### Attack Phase
- Effects that occur before your Attack Phase begin now (such as Burn damage).
- Unit passive abilities typically happen during this turn. For example, if the Wraithwitch can summon a Wraith during this turn, summon it during this Phase before any unit you control attacks (this balances setups and prioritizes Spells over passives).
- Any Troop, Building, Minion, and Emperor you control can choose to make a valid attack.
- Any unit you control with an activational ability may activate its ability (Emperors cannot activate more than one ability per turn).
- Unit mechanics or passive abilities that rely on attacking happen during this Phase.
- Any Troop or Building you control that does not make an attack grants a Fractal once this Phase ends.
### End Phase
- Once you enter this turn, you may no longer take any summon or battle actions.
- Effects with before your End Phase begin now.
- This Phase marks the end of your turn.
- You may draw 2 cards from the top of your deck for free. If your hand has 6 cards, draw 1 card. If your hand has 7 or more cards, draw none.
- You gain 2 Gerzite during this Phase (3 during Overtime, 4 during Double Overtime).
- Cards with timers (activation timers and limit timers) typically decrease now (Tome activations, Biome timers, Spell timers) unless specifically stated.
- Effects that take place at the end of your turn or say after your turn ends begin now (such as Building SP decay).

You naturally gain 4 Gerzite per turn

## Turn Timeline

BEGINNING — Overdraw Phase — Summon Phase — Attack Phase — End Phase — END

### Overdraw Phase
- Gain 2 Gerzite
- Untap all cards that were tapped on your previous turn
- Overdraw, if you wish
- Discard cards from your hand to make room, if you wish
- Cards with timers stating “at the beginning of your turn”
- Bleed status effect decreases
- Overheal timer decreases
### Summon Phase
- Cure Entrenched
- Summon Units
- Units with Ambush may attack and remove the status effect
- Play Spells
- Cards with “Entrance:”
### Attack Phase
- Cards with “Passive:”
- Attack with eligible cards
- Play Spells
- Emperor abilities
- Stun status effects take place
- Burn status effect
- Decay status effect decreases
- Collect eligible Fractals
### End Phase
- No more summoning cards or playing Spells
- Cards with timers stating “at the end of your turn”
- Building SP decay
- Frenzy status effect decreases
- Divinity status effect decreases
- Damage Boost status effect decreases
- Overclock status effect decreases
- Overshield status effect decreases
- Thorns status effect decreases
- Buffer status effect decreases
- Regeneration status effect decreases
- Freeze status effect decreases
- Weakness status effect decreases
- Possession status effect decreases
- Support timers decrease
- Biome timer decreases
- Draw 2 cards
- Gain 2 Gerzite
- Turn amount goes up by 1

## Drawing Cards

Your End Phase marks when you can passively draw cards. During your end phase, you may draw 2 of the top cards of your deck if your hand has less than 6 cards.

- If your hand has 6 cards, draw 1.
- If your hand has 7 or more cards, draw none.

The Overdraw Phase (beginning of your turn) grants you 2 Gerzite and allows you to spend 1 Gerzite to draw an additional card (up to a maximum of 10 cards in your hand) to be in your hand during your Summon Phase. Any card effect that allows you to draw cards counts as an Effect Draw and does not require you to spend Gerzite if you go above 7 cards.

- Even with Overdraw or Effect Draw, your hand can never reach above 10 cards total.
- If you have leftover drawing actions from an Effect Draw after reaching 10 cards, you may not take those actions, discard the leftover draws and gain 1 Gerzite for each card discarded this way, or place the leftover draws at the bottom of your deck and gain 1 Fractal for each card placed at the bottom this way.

## Overdraw & Effect Draw

At the beginning of your turn, during the Overdraw Phase, you may choose to draw additional cards as a way to have more cards at the start of your Summon Phase.

- You gain 2 Gerzite during this Phase, which you may choose to spend or keep.

Overdrawing is the only way to increase your passive-draw-locked hand from 7 to 10 cards.

- 10 is the absolute maximum in the game, no matter what.
- If you choose to Overdraw, you must pay in Gerzite. Each Overdrawn card costs 1 Gerzite.
- During your Overdraw Phase you may also discard cards from your hand into your discard pile to open up room in your hand. You gain nothing for them.
- The table plays the rules for you: drawing, searching or looking through your piles only happens when a card effect or mechanic lets you, and the card does it for you (Land Looker, for example, opens your deck for you to choose a Biome).

Any Spell or ability effect that allows you to draw cards is called an Effect Draw. Effect Drawing is functionally the same as Passive Drawing.

- If an Effect Draw causes your hand to go over 7 cards, you do not have to pay Gerzite like in Overdraw.

Spell or ability effects are the only way to draw cards after your Overdraw Phase ends and before your End Phase begins. If an Effect Draw would cause you to have over 10 cards, you may:

- Not draw them.
- Place them at the bottom of your deck and gain 1 Fractal for each card placed there.
- Discard them and gain 1 Gerzite for each card discarded.

Your Summon Phase follows the Overdraw Phase.

## Summoning Cards

During your Summon Phase, you may cast any Spell or summon any unit that you can afford. You may also choose not to do anything and turn the battle over to your Attack Phase, saving Gerzite.

- Summoning a Spell does not end your Summon Phase.
- Summoning a Support does not end your Summon Phase.
- Cards with special effects or entrance effects that play upon being Summoned do not end your Summon Phase.
- Attacking with a unit or your Emperor ends your Summon Phase and begins your Attack Phase.
- Using an Emperor ability ends your Summon Phase.

Your Attack Phase follows the Summon Phase.

## Attacking

The primary goal is to defeat your opponent's Emperor. To do this, attack your opponent's units and create openings on their side of the field.

- Each player has 5 lanes. If even one lane is open, the opposing player's Emperor may attack yours!

Some units (Troops & Buildings) and Spells (but not all) may also be able to damage the Emperor (or Minion).

- They must specifically state this, or have the Direct target type attribute (for units, the lane in front of them must also be unoccupied).

Most units can only attack the lane directly in front of them.

- Units with range values may attack adjacent lanes.
- Some cards give attacks a chance to miss (Fog, Smog, Ash Druid, some Biomes). The table rolls it as the attack arrow is drawn: a missed attack deals no damage. Chances from several cards stack: the attack misses if any one of them does.
- Ground units must have a range value in order to attack Flying units.

Each unit you control may declare 1 attack per turn (unless it has specific mechanics that dictate otherwise).

- Units cannot attack on the turn they are summoned. This is due to a status effect called Entrenched. Units with Entrenched receive 1/2 damage from all sources (excluding status effects) until they no longer have Entrenched.

No unit you control (regardless of special mechanics or status effects) may declare an attack on your first turn.

- You may not cast any Spells or deal Spell damage on your first turn.

Units you control that have exhausted their attacks for the turn are tapped and flipped to their side.

- Units with Entrenched are played flipped upside down.
- Any unit that has a declarable attack that is not used during a turn has its attack converted to a Fractal.

## Gerzite

Gerzite is the resource system used to play cards and use abilities.

- A player may have a maximum of 20 Gerzite.
- Each player starts the game with 10 Gerzite.
- You gain 2 Gerzite when the playing field is turned to you, and you gain 2 more Gerzite when your turn ends and you pass the playing field to your opponent (4 natural Gerzite per turn).
- You gain more Gerzite during Overtime (6 natural Gerzite per turn), split into 3 Gerzite before and 3 Gerzite after your turn.
- You gain 1 Gerzite for every 5 Fractals you obtain.
- A Gerzite or Dark Gerzite token on your bar is your resource. One lying on the table instead is a counter: it holds an amount, and it can be set to add that amount to your bar (or remove it) as each of your turns begins or ends, for as many turns as its timer says. This is how cards like Tome of Gerzite (1 extra Gerzite at the beginning of your turn while it is active) are kept track of. Drag a counter onto your bar to turn it into that much Gerzite.
- Dark Gerzite is a specialized version of Gerzite used to summon cards with stronger attacks, yet weaker defense.

## Fractals

After your turn ends, any Troop or Building that you control that has no negative status effects and has not been tapped grants you one fractal. Once you collect 5 Fractals, you gain an additional Gerzite, and your Fractals reset to 0.

- Fractals are not reset and are carried over between turns. They reset only when you reach or exceed 5, granting 1 Gerzite.
- If you gain Fractals after your turn that put your Fractal count above 5, you gain only 1 Gerzite and all Fractals are lost (no overflow).
- Emperors and Minions do not count for Fractals. Therefore, it is best to always use their attacks when available.
- Summoned units (units a card summons onto the field, such as a Necromancer's Skell-Minis) do not earn Fractals either.
- Fractals cannot be destroyed, taken, or otherwise interacted with by any player, except through their intended method of acquisition.

## Deck-Out

If you are forced to draw a card from any source (Passive Draw and Effect Draw, but not Overdraw), and you do not have sufficient cards remaining in your deck, your Emperor will take 1 damage point (unblockable) for each card you cannot draw. Example: If you have 3 cards left in your deck, and you play a Spell that requires you to draw 4 cards, you will draw the remaining 3 cards from your deck and place 1 damage counter on your Emperor. At the end of your turn, you will take 2 additional damage, since you can no longer Passive Draw from your deck.

- If your hand was at 8 cards, and Effect Draw in this situation left you with 10 cards in your hand, your deck would have 1 remaining card. You would then take no Deck-Out damage during your turn (assuming your hand stays at 10 cards).
-- Maintaining a 10-card hand is the only way to bypass Deck-Out damage.

## Overtime

Overtime is a game mechanic that speeds up the game. After both players have had 12 individual turns (12 global turns), Overtime will begin.

- Natural Gerzite gained goes up during Overtime. You gain 3 Gerzite during your Overdraw Phase, and 3 Gerzite during your End Phase.
- Both players' Emperors take 1 damage point (that bypasses all forms of Shield, Buffer, Divinity, etc.) at the end of each turn.

### Double Overtime
At global turn 20, Double Overtime begins, to hasten games that have run this long.

- You gain 4 Gerzite during your Overdraw Phase, and 4 Gerzite during your End Phase.
- Both players' Emperors take 2 damage at the end of each turn.

## Winning and Ending a Game

- When an Emperor's health reaches 0, the game ends and the other player wins. (In 2v2, a team loses once both of its Emperors have fallen.)
- If every Emperor falls at once (Overtime damage at the end of a turn, say), the player (or team) who dealt more damage to Emperors with attacks and Spells wins. Overtime and Deck-Out damage don't count toward this. If that is even too, the game is a draw.
- **End it here** lets you forfeit, claim a win, or offer to call the game where it stands. A called game is won by the side whose Emperor has the larger share of its health left (a percentage, so an Emperor with more health doesn't win just for being bigger). If both have the same share left, the side that dealt more damage to Emperors with attacks and Spells wins; if that is even as well, it's a draw.
- Bots answer End it here too: they take a forfeit, and they agree to call the game when it would win it for them. Asked to give the game up, or to call it while they are behind, they only agree when they think they are clearly losing (the stronger the bot, the better it judges that). A bot may offer to call the game itself when you are clearly losing, but never before global turn 6, and it waits at least 3 of its turns before asking again.
- **Leaving part-way:** if you leave an online game before it ends (you close the page, lose your connection, or leave the table), you have 2 minutes to come back with Rejoin Game. If you don't, you lose by forfeit, and against a bot, the bot wins. That game counts toward the card stats like any other.
-- In 2v2, if your teammate is still playing, you drop out instead, as if your Emperor had fallen. Your Emperor leaves the table, and your teammate takes over your lanes and every card you left in play.
-- If every player leaves and nobody comes back in time, the game ends in a draw. It still counts toward Use and Deck Use once it has reached the global turns the card stats need, but never toward Win or Deck Win.

## Battle Timer

The Battle Timer keeps 1v1 and 2v2 games moving. Ask for it with the **Battle Timer** button at the top right of the table, beside the Game Menu.
- Your opponent has to agree before it goes on. In 2v2, both players on the other team have to agree. Bots always agree.
- While it is on, each player has **1:30 for each of their turns**. The time starts fresh at 1:30 every time the turn comes back to you.
- When you have 30 seconds left, you are warned and the timer turns red.
- If your time runs out before you pass the turn on, you lose by forfeit (in 2v2, your team does).
- Bots' turns aren't timed: they play quickly anyway.
- If the player whose turn it is leaves the game, their turn clock pauses and the 2 minutes they have to come back count down instead. If they come back in time, their turn clock carries on from where it stopped.
- Turning the Battle Timer off works the same way: ask, and the other side has to agree.

## 2v2

Two teams of two play on a wider mat. Everything else follows the normal rules, with these changes.

### The table
- There are **4 lanes** instead of 5. On each side, the left player controls Lanes 1 and 2, and the right player controls Lanes 3 and 4.
- Each player has their own Emperor (and Minion slot), a Support slot and **3 Spell slots** (instead of 4).
- There is still only **1 Biome slot**, shared by everyone.

### Turns
- Both players on the blue team take their turns (left player, then right player), then both players on the red team. That is **one global turn**.
- Each player has their own full turn: Overdraw, Summon, Attack and End Phases.

### Gerzite
- Teammates share **one Gerzite bar (20)** and **one Dark Gerzite bar (10)**.
- Teammates also share **one Fractal count**: both players' Fractals go on the same stack, and every 5 of them give 1 Gerzite to the team bar.
- A team gains **7 Gerzite** per team turn: 2 as its turn begins, 1 as the first teammate passes the turn to the second, and 4 as it ends. (With a teammate out, there is no pass and no 1.) As in 1v1, the team that goes first gets no Gerzite at the start of its very first turn, so the second team gets the first gain of 2.
- Each team starts with 16 Gerzite on its bar.

### Teammates
- Teammates can see each other's hands at any time (the button beside "Your hand").
- To keep the game moving, teammates may place counters on each other's cards.
- Your teammate's Emperor, Units and Spells are theirs to use: you can't attack with them, tap or use them, move them, or sacrifice them, and you can't play or move cards into your teammate's half of the board (their lanes, Support and Spell slots).
- The one exception is **Calling Pillar**: you may play it into your teammate's lanes too. In 2v2 the call needs a Pillar of yours in each of the 4 lanes on your side, and a fifth in one of them (5 Pillars, as in 1v1).

### When an Emperor falls
- A player whose Emperor has fallen is **out** at the end of that turn (unless it is healed before then).
- Their teammate plays on alone and may use **all 4 lanes**, but not the fallen player's Support or Spell slots. Everything the fallen player left on the table is now the teammate's to use: they may attack with those Units and use any of their Spells still in play.
- What the fallen player left on the table keeps going on their teammate's turns: it untaps, its statuses (Burn, Bleed…), counters and timers tick, and its start- and end-of-turn effects happen, all on the teammate's turns. Die rolls the fallen player still owed are rolled by their teammate.
- If both Emperors on a team have fallen, the game ends at once.
- The fallen player watches their teammate: they can see their teammate's hand and the table, but cannot act, and cannot search their own deck or discard pile.
- When both Emperors on a team have fallen, the other team wins.

### Overtime
- Overtime begins after **8** global turns (instead of 20).
- During Overtime, each team gains **11 Gerzite** per team turn: 4 as it begins, 1 as the turn passes between teammates, and 6 as it ends.
- At the end of each team turn, every Emperor still in the game takes 1 damage.

= Your Deck and the Table

## Deck Size

Each player must have a deck that consists of 61 cards.

- 1 Emperor
- 60 non-Emperor cards.
- Some cards may be limited to a specific amount within a deck.

The suggested main deck structure is:

- 30 Troops and Buildings.
- 20 Spells.
- 10 Supports.

This does not need to be followed, and you could have a deck of 60 spells if you wanted (why would you ever do that?).

## Deck and Discard Piles

Your deck is where you draw cards from to place in your hand.

- Shuffle your deck at the start of the game and place it upside down on your field.
- Your starting hand is 7 cards: 5 Troops or Buildings and 2 Spells or Support cards (never more than 2). It never includes a Win Condition card or a card that costs more than 8 Gerzite. Any you would draw are left in your deck, which is shuffled again, so you can draw them later. If your deck can't make a hand this way, these limits are loosened just enough to deal 7 cards.

Your discard pile is where destroyed and removed cards go.

- Your discard pile remains face-up on the field. Place the most recently discarded card on top of your discard pile.

## Rarities

There are nine rarities: Common, Uncommon, Rare, Ultra-Rare, Epic, Mythical, Legendary, Grand, and Ancient.

- Each rarity, when played right, can counter another rarity.
- Rarities identify more complex mechanics or cards with qualities that can turn the tide of a battle when used correctly.

Rarities also limit how many cards you can include in your deck. While there is no limit to the number of a rarity you may have, rarities have differing limits on the number of copies of a specific card you may have. The amount starts at 12 for Common, as seen below, and decreases as follows:

- You may have a maximum of 12 copies of the same Common card in your deck.
- You may have a maximum of 12 copies of the same Uncommon card in your deck.
- You may have a maximum of 10 copies of the same Rare card in your deck.
- You may have a maximum of 10 copies of the same Ultra-Rare card in your deck.
- You may have a maximum of 8 copies of the same Epic card in your deck.
- You may have a maximum of 5 copies of the same Mythic card in your deck.
- You may have a maximum of 4 copies of the same Legendary card in your deck.
- You may have a maximum of 3 copies of the same Grand card in your deck.
- You may have a maximum of 2 copies of the same Ancient card in your deck.

For example: you may have a deck full of 60 Ancient cards, if you wish. But you cannot have 3 Twilight Seedling Troops or 5 Arcane Explosion Spells in your deck. You may have a maximum of 2 of each.

## Emperor

Your Emperor is the core unit of your entire deck, and sits behind your main playing field in the Emperor slot.

- If your Emperor dies, you lose the game.

Your Emperor has health, can attack, and also has two activational abilities it may use at any point to help change the tide of a battle. Emperors may attack once per turn and can target any unit your opponent controls. Your Emperor may only target the opposing Emperor (or minion) if they have at least one empty lane.

- Emperors have 2 turns of Entrenched at the start of the game, meaning they cannot attack or use abilities until a player's third turn.

During Overtime, both Emperors take 1 damage at the end of each player's turn (2 in Double Overtime, from global turn 20).

- Emperors also take 1 damage for each Passive Draw that you cannot complete during your End Phase.

## Minion Slot

The Minion slot is an area linked to your Emperor where you may play a Troop card. This Troop may attack any opposing card your opponent controls once per turn, similar to your Emperor.

- Like your Emperor, the Minion may be targeted only by units with a Direct damage target type.

Minions deal half damage, rounded up to the nearest whole number, to any unit they attack.

- Lanespanners cannot be played into the Minion slot.

Your Emperor and Minion receive linked damage and effects (if applicable). Any damage the Emperor takes is mirrored onto the Minion, and vice versa.

- Unlike your Emperor, which can only be targeted by select Spells, the Minion may be targeted by any Spell, as any other Troop could.
- This means the Minion lets opponents deal Spell damage to your Emperor that they otherwise may not be able to.
- While your Minion takes full damage from any Spell, your Emperor takes only half (rounded to the nearest whole).
- Any recoil damage from a Minion is mirrored onto the Emperor, and vice versa.
- Enemy-inflicted Status Effects are mirrored on the Minion and Emperor.
- Friendly-inflicted Status Effects are not mirrored on the Minion and Emperor (they must be buffed separately).
- A hit soaked up by the Emperor's or the Minion's Shield (or Overshield) is shared too: the other one's Shield or Overshield takes the same hit (or its health, if it has none).
- A Minion that summons units into its own lane (a Necromancer's Skell-Minis) lays them out beside it in the Minion slot, at the smaller size. They are not the Minion: they aren't linked to your Emperor.

Because of the Minion's double-edged nature, you don't need to have one in play. The Minion gives you an extra way to deal damage to any unit your opponent controls, but it can also give foes an extra way to inflict damage on your Emperor.

## Biome Slot

The Biome slot is shared by both players. Any Biome put into play shares its effects with both sides of the field, including buffs and debuffs.

- Biomes have a turn count that indicates how many turns they are active before being forcibly discarded.
- Either player may move any Biome early by paying the stated discard amount in Gerzite. This is the only way either player may end the effects of a Biome early.
- The Biome card, while shared by both players, is naturally played facing the player who placed it.

The turn count indicator decreases during the controller's End Phase.

## Support Slot

The Support slot is the only place where Tomes and Trigger cards may be played.

- You can only have 1 Support card in the Support slot at a time.

## Spell Slots

Each player has four Spell slots, where Spells must be placed in order to be played. You cannot play a Spell if there are no open Spell slots.

- Spells with multi-turn effects stay in a Spell slot until discarded.
- When a Spell is discarded, move all other Spells up one slot to open previous slots.
- Even if a Spell has an instantaneous effect and is immediately discarded upon use, it must enter a Spell slot before executing its effect.

= Cards and Mechanics

## Card Type

Cards fall into several types: Emperor, Unit, Spell, and Support. Emperors are the central part of your deck, and protecting it is your main goal. If your Emperor is destroyed, you lose. Units can either be:

- Troops, the basic attacking units that form the backbone of your deck.
- Buildings, which are typically stronger than Troops individually but lose health over time.

Spells are cards you can play to alter the outcome of interactions. Spells must be placed in one of the four Spell slots on your side of the field, whether the Spell provides instant effects or over-time effects. Support cards can either be:

- Biomes, which have their own slot shared by both players. Any player may place or remove a Biome, as they provide buffs and/or debuffs to certain cards on both sides of the playing field.
- Tomes are magical books with special effects that aid your side of the field for a certain number of turns. You may extend their effects by paying additional Gerzite. Once a Tome's turns are over, you have until the end of your next turn to pay to restart it; if you don't, it is discarded automatically as that turn ends.
- Triggers, which are played face down from your hand into the Support slot. Once your opponent takes an action that meets the Trigger's requirements, flip it and discard it, and the player receives a payout in Gerzite: the Trigger's own cost plus 2.

## Affiliation

You can identify affiliation types by the color of a card's nameplate and description text box.

- Affiliation types can interact through special mechanics a card may include.

Example: a certain unit may deal double damage to Spectral Troops, but only half damage to Neutral Troops.

Affiliation types: Duned, Forested, Frigid, Magical, Neutral, Spectral, Submerged, Undead, and Volcanic.

## Tiering Up

Tiering is an in-game way to increase a card's power level through raw stats. A card can be tiered up by placing two or three copies of the same card down at once, on top of each other, as "one card." Each card has a box at the top that details the stat boosts gained at each tier.

- Tier II cards receive the stated buff.
- Tier III cards receive the stated buff, as well as the listed Tier II buff.

How high a card can tier, and what each tier gives, depends on its type:

- **Troops and Buildings** go up to Tier III.
-- Tier II: +20% health (SP for a Building).
-- Tier III: +20% damage, on top of Tier II's health.
-- A fraction always rounds up (8 health at Tier II is 10, not 9.6).
- **Spells** go up to Tier II. Merging a Spell up to Tier II gives you 1 Gerzite back (the box reads "1 Gerzite Less"). Spells that cost 0 Gerzite have no tiers.
- **Biomes** go up to Tier III. Each tier raises the Biome's removal cost: +1 at Tier II, +2 in total at Tier III.
- **Every other type** (Emperors, Tomes, Triggers) has no tiers.

Cards can only be tiered up when played or summoned, not when they are already summoned on the battlefield.

- Therefore, tiering up serves as an alternative way to play a card you have multiple copies of in your hand.
- On the table, to make this easier, play the first copy as normal, then drop the next copy from your hand on top of it. This counts as playing them together. Only do this as part of playing the card: don't tier up a card that has already been on the battlefield.

Tiered-up cards do not increase in Gerzite price. When a tiered-up card is destroyed, place all copies used to tier the card up in the discard pile.

- A card can't be tiered up while it has any damage on it, or any status effect other than Entrenched or Ambush. (A counter the card comes with from its own stats, like Clay Soldiers' Buffer, doesn't count.)
- A swarm is tiered up as a whole: every one of its Troops takes the new tier. It can only be tiered up while all of its Troops are still on the field, and none of them is damaged or has a status effect.

- Discarding a higher-tiered card means more copies of that specific card are inside of your discard pile. This may help, as that card now has a higher chance of being repulled from your discard pile if you play a card that lets you do so.

## Darkgale & Dark Gerzite

Darkgale is a special mechanic that lets a player summon stronger but more fragile versions of their Troops and Buildings. Players must summon Darkgale units using Dark Gerzite, indicated by a special resource bar with a red-Gerzite symbol. Darkgale units deal double damage but take double damage from all sources.

- This means that a Darkgale Troop receives normal x1 damage (instead of half) when newly summoned and affected by Entrenched.
- Darkgale buildings will lose 4 SP per turn, instead of 2 SP.
- Damage effects, including those with turn counts, are not doubled.
- Only specific numerical values, such as damage dealt, healing dealt, Shield given, etc., are doubled.
- Status effect damage is counted separately and is not doubled by the effects of a Darkgale attack.

Spells may be Darkgaled, doubling their numerical stats.

- Damage dealt, healing dealt, Shield given, etc., not turn counts.
- Status effect damage is counted separately and is not doubled by the effects of a Darkgale Spell.
- Darkgale Spells deal x1 (instead of half) damage to Emperors.

Darkgale Spells, unlike Darkgale units, must be summoned with twice the required Gerzite cost in Dark Gerzite.

- This means that no 6-cost Spell or higher may be summoned as a Darkgale Spell, as they would require 12 Dark Gerzite, which is impossible to acquire.

Darkgale cards are played by spending the card's written Gerzite cost in Dark Gerzite.

- Some cards require Dark Gerzite to be played from the hand. These cards cannot become Darkgaled, as they require Dark Gerzite as a base resource, not a supplemental one. Therefore, the stats and effects written on these cards are as stated.

You can obtain Dark Gerzite through Spells or special mechanics. However, the main way to obtain it is through sacrifice. You may sacrifice any number of active (not affected by Entrenched) units you control during any one of your turns. This equation [Current Health / Max Health x Gerzite Cost, rounded to the nearest whole] tells the player how many Dark Gerzite they will obtain from the sacrifice.

- Shields do not count as health for sacrifice.
- Spells cannot be sacrificed.
- You can obtain more Dark Gerzite than the card's Gerzite cost if the sacrificed unit is affected by Overheal or has additional health.
- Cards with multiple units are treated as one singular unit when sacrificed. For example, Skell-Minis, who each have 1 health, are treated as one troop with a maximum health value of 3. If all three Skell-Minis are alive, the current health value will be 3 (if two are alive, then 2, etc.). When sacrificed, the entire Skell-Minis card is discarded.

Each player may only hold a maximum of 10 Dark Gerzite, meaning some cards may be impossible to summon as Darkgale versions. This can, however, be circumvented with specific Spells or effects that directly Darkgale a card already on the field.

## Entrance

An entrance effect is a special mechanic that happens immediately when a unit is played.

- Entrance effects can happen immediately, even if the unit they are applied to is under the effect of Entrenched.

Entrance effects can be retriggered if the card is replayed.

- Entrance effects are only triggered when a unit is summoned, and are not replayed if the unit is simply moved lanes.

## Passive

A passive effect is a special mechanic that happens as stated, unless specified, without needing a tap action.

- Passive effects cannot happen if the Unit is affected by Entrenched, Comatose, Frozen, Locked, or Stunned.

Passive effects cannot be passed.

- Passive effects must happen at the beginning of your attacking phase.

## Inherent

An inherent effect is a special mechanic that is always part of the card. It works like a passive effect, except it can never be turned off.

- Inherent effects still work while the Unit is affected by Entrenched, Comatose, Frozen, Locked, Stunned, or any other status effect.

Inherent effects cannot be passed.

## Tap

A tap effect is a mechanic that requires a tap action to execute.

- Executing a tap effect means a Unit cannot attack until untapped.
- Units with Frenzy may choose to execute their tap effect instead of one or both of their attacks.

## Ability

Abilities are special mechanics that Emperors have. Each ability costs Gerzite to activate and has a specific cooldown time.

- Abilities can deal damage, provide defense or healing, or support the deck.

## Health

Health indicates how much damage a unit can take before it is destroyed. Health is displayed by a heart symbol. A Building's health is called SP (Structure Points). SP functions the same way as health, representing how much damage the Building can still take. However, SP decreases by 2 after each turn the Building is still alive (SP does not decrease on the turn the Building is played).

- Unless specified, SP cannot be healed as a Troop's health can.

## Building SP

A Building's health is called SP (Structure Points). SP functions the same as health in terms of how much damage the Building can still take. However, SP decreases by 2 after each turn the Building is still alive (SP does not decrease on the turn the Building is played).

- Unless specified, SP cannot be healed, unlike a Troop's health.

## Damage

Damage is the attack power of a card. Damage is specified by a sword symbol.

## Damage Type

Cards have different damage types that describe how they apply damage to a target. Emperors, Troops, and Buildings:

- Single Damage: The card deals one instance of simple damage to the selected target.
- Splash Damage: The card deals damage to all units within the targeted lane. Ground Troops cannot damage Flying Troops with splash damage, unless they have Range.
- Stat Alter: The card changes the stats of the target.
- Multi-Hit: The card possesses an attack that deals damage in multiple stages.
- Healing: The card heals the target with its attack.
- Misc.: Anything not specifically fitting into a single category.

Spells:

- Single Damage: The Spell deals one instance of simple damage to the selected target.
- Splash Damage: The Spell deals damage to all units within the targeted lane.
- Stat Alter: The Spell changes the stats of the target.
- Movement: The Spell provides movement or positional-altering effects.
- Healing: The Spell heals the targeted card.
- Misc.: Anything not specifically fitting into a single category.

## Target Type

Cards have different target types, which detail how their damage can be applied to a targeted card.

- Target: The default form of attack targeting. The attacking unit may pick another targetable unit and deal damage to it. Units with Target may not damage Emperors, and must have range in order to attack Flying units. If a card is not Target type, but does not specifically state that it possesses Pierce, Direct, etc., treat the unit as Target.
- Pierce: Similar to Target, but cards ignore the Buffer and Shield attributes.
- Direct: Similar to Target, but units with Direct may also attack the opposing Emperor or Minion if the lane in front of them is empty.
- Area of Effect: Generally provides an effect over a certain area of the playing field rather than on a certain targeted unit.
- Field Effect: Provides an effect over one or both sides of the entire battlefield rather than on a certain targeted unit.
- Resource: A very rare target type which specifically targets resource reserves, such as Gerzite, Dark Gerzite, or the player's hand.

Spells have a secondary target type that specifies what they can target.

- Single: The Spell may target one specific unit your opponent controls. Single secondary target types are usually (but not always) the Target form of damage. Splash Single damage types exist and essentially function as Lane (see below) secondary target types; however, they are separated for certain specificities and nuances.
- Multi: The Spell may target multiple different units. The number of units that may be targeted is specified in the Spell's text
- Lane: The Spell may target an entire lane. This secondary target type usually results from splash damage.
- Player: Allows a Spell to target a player--usually seen in Spells that deal with player hands or decks.

## Range

Range lets a unit target adjacent lanes, not just the one in front of it. Range is indicated by a bow and arrow symbol.

- A ground unit needs range to damage a flying unit.

Emperors can attack any unit, and therefore do not have range.

## Flying

Flying units are typically weaker than ground units in terms of health and damage. However, only ranged units can damage them. All Spells (unless specifically stated) can target flying units.

## Ground

Ground units make up the majority of all Troops and Buildings. Ground units require range (unless specifically stated) to target flying units.

## Shield

Shield is a secondary defense bar that is applied over health. Shield is indicated by a shield symbol. Shield has the unique property of damage absorption. Any excess damage left over from attacking a Shield is lost. Example: If a Troop with 20 health is attacked by something with 10 damage, it will have 10 health. If it has 1 Buffer, it will take only 9 damage and have 11 health left after the attack. However, if the Troop has 1 Shield, the Shield absorbs all 10 incoming damage, breaks, and leaves the Troop with 20 health. Shield can be bypassed by Pierce damage (Direct damage does not bypass it).

Shield is one layer, however big it is. Any Shield a card gains is added to the Shield it already has. Each hit is soaked up by the Shield in full: the Shield loses that much, and breaks once a hit is as big as what is left of it (the rest of that hit is lost). Example: a Troop with 3 Shield hit for 2 is left with 1 Shield and no damage; hit for 3 or more, its Shield breaks and it still takes no damage. A card that says it has separate layers of Shield (Ghostly Armor, Ice Golemite) has that many Shields, and each hit breaks one of them.

## Thorns

Thorns is a unique property that reflects a certain amount of damage back to an attacker. Thorns is indicated by a spiny symbol. Thorns does not reflect damage from Spells. In order for damage to be reflected by Thorns, it must have come from an individual source (an Emperor, a Troop, or a Building). If an attacker is a Multi Troop or has an attack with multiple phases, each instance of damage will activate the defender's Thorns.

## Count

Count is an attribute applied to Multi Troops, which specifies the number of Troops a certain card may contain. Example: Skell-Minis is a Troop card with 1 health and 1 damage. However, it has a count of 3 (denoted by an X symbol at the top of the card). This means the Skell-Minis card is actually three individual Troops, each with 1 health and 1 damage.

- On the table, a card with a count takes the field as that many copies of the card in its lane, one for each Troop (a swarm). Skell-Minis is three Skell-Mini cards. The card goes to your discard pile when the last of them is destroyed.
- Each Troop in a swarm is an individual Troop: it has its own health, counters and status effects, it can be targeted on its own, and an effect on "one Unit" affects only that Troop. Splash damage reaches every Troop in the lane. A move effect on one target Unit moves only that Troop; one that moves every Unit in a lane moves them all.
- Every alive Troop in a swarm attacks on its own, one instance at a time, and can choose its own target (if able). For example, a Skell-Minis with two alive Troops deals two instances of 1 damage.
- A swarm attacks together: once one of its Troops has attacked, the rest must attack that turn too. Any that have not but could have (even with nothing left to attack) are tapped as your Attack Phase ends, so they earn no Fractals. One that was unable to attack (Stunned, Frozen, Locked…) is not tapped. An idle swarm earns 1 Fractal.
- Sacrificing any Troop in a swarm sacrifices the whole swarm, for the Dark Gerzite the swarm is worth as one card.

## Lanespanner

Lanespanner Troops are Multi Troops which, instead of being played together in one lane, are played right next to each other in connecting lanes.

- Lanespanners may bypass the "one summoned unit per lane" rule, so long as the middle of the Lanespanner troop is played within an empty lane.
- Lanespanners cannot be played if an edge Troop is outside an edge lane.
-- On the 2v2 table (4 lanes a side), Flesh-Eating Locusts (5 units) is played in one of the two middle lanes. Its units fill all 4 lanes, and the one that would go off the end joins the unit in the end lane, like Undead Legion's soldiers.
- The card played from your hand is the main card. The extra units placed in the other lanes are its summoned units (Royal Line's extra units are Royal Guards; Dark Order's are Dark Order Soldiers; Flesh-Eating Locusts' are Flesh-Eating Locusts; Clay Soldiers', when played as a Lanespanner, are Clay Soldiers). Each carries a Lanespanner token.
- Because they are summoned units, the extra units may go into lanes that already hold a unit. They share the lane (see Summoned Units).
- A Lanespanner attacks together, like a swarm: once one of its units has attacked, the rest must attack that turn too. Each picks its own target. Any that have not but could have are tapped as your Attack Phase ends, so they earn no Fractals. (A Leviathan's Body Segments never attack, so this never applies to them.)
- Sacrificing the main card also destroys the extra units it spread. You gain Dark Gerzite for the main card only.

Example: a Lanespanner with 4 Troops cannot be played in Lanes 1 or 5, as one of the Troops would not be able to be played in a connecting lane because it would be off the edge of the playing field.

## Summoned Units

Some cards summon other units onto the field: a Necromancer's 1/1 Skell-Minis, a Frozen Scarecrow's Icewings, the Royal Guards of Royal Line, a Leviathan's Body Segments. These are summoned units. Each has its own small card (marked "Summoned Troop") that is never in a deck. Take it from the Spawn menu.

- A summoned unit uses the stats on its own card. It only has a description when it does something of its own (a Gerzite Crystallite, a Hexed Mummy, a Leviathan Body Segment).
- A summoned unit is its own card even when a normal card of the same name exists. A Chicken from Chicken Barrel is a 4/5 summoned Chicken; a Frozen Scarecrow's Icewing is a summoned Icewing.
- Gerzite Crystallites and Gerzite Clusters are summoned one unit per card: summon as many cards as there are units.
- A summoned unit leaves play like any other unit, but it is never put into a pile or a hand.

### Sharing a lane

Summoned units (and clones) may be summoned into a lane that already holds a unit. The lane is shared: the cards in it shrink to half size and sit side by side (three or four sit in a grid). When a card is the only one left in its lane, it goes back to full size.

- A shared lane still holds only one ordinary unit. Summoned units and clones don't take up its room.
- Counters and status tokens on a card move and shrink with it.
- In a game, counters, status tokens and cards in play can't be changed, deleted, moved, turned or flipped by hand: the table does it for you, or a card effect lets you. (The Solo Table is a sandbox where all of this can still be done by hand.)

### Attaching to a card

A summoned unit can be attached to the card that summoned it instead of taking room in the lane: drop it on the bottom edge of that card. It shrinks to a small card along that edge and follows the card wherever it goes, and the card stays full size. This shows at a glance which units a card has summoned.

- Hover over a small card to see it full size, as with any card. Its tokens shrink with it.
- Drag it off the card to detach it. If the card it was attached to leaves play, it is detached.

### The Leviathan's Body Segments

A Leviathan Body Segment arrives with a Link counter, and puts a matching Link counter, a +2 Health counter and a +1 Range counter on its Leviathan. The Link counters show which Leviathan each segment belongs to.

### Undead Legion (Bones & Bruises)

Undead Legion's card is its General: 3 health, 3 damage and 3 Shield. It can only be played into the middle lane (Lane 3; in 2v2, either of the two middle lanes), and that lane must be empty. As it arrives it plants a battle banner and raises its Legion: two 2/2 Undead Soldiers in its own lane and one in each of the two lanes either side of it. On the 2v2 table a soldier that would land off the edge goes into the end lane with the soldier already there. They are summoned units, so they may share a lane with other cards.

- The General and its soldiers attack together, like a swarm: once one of them attacks, the rest must attack that turn too, or are tapped as your Attack Phase ends. A Legion earns one Fractal, and only if every one of its units (General, Undead and Spirit Soldiers) is untapped.
- An Undead Soldier destroyed while its General is still standing rises again as a 2/2 Spirit Soldier (Spectral) in the same spot. A Spirit Soldier can never be damaged and can't be targeted or attacked. It doesn't fill its lane (that lane counts as empty for summoning and for Direct attacks), and effects that destroy Units without targeting them (Split Chasm, Tectonic Shift) pass it by. It still attacks, can be given status effects, and moves with its lane.
- When the General is destroyed, every Spirit Soldier fades away at once. Undead Soldiers stay on the field, and from then on they are destroyed for good. An Undead Soldier destroyed at the same moment as its General doesn't rise.
- Sacrificing the General sacrifices the whole Legion. It is worth 6 Dark Gerzite at full strength: the General's health and Shield plus its Undead Soldiers' health (18 in all) make 6, and it scales down with what has been lost. Spirit Soldiers count for nothing. The soldiers can't be sacrificed on their own.

## Clones

Some cards make a copy of a unit: From the Mirror Realm, Xolox's Spectral Clone, the Illusionist, and Reflective Orb's stand-in copy. The copy is a clone: a light blue version of the card. Hover over it to see what made it (for example, "Skell-Minis: cloned from From the Mirror Realm").

- A clone from From the Mirror Realm has 1 health, keeps all other stats, gets the status effects the unit had, and is Entrenched.
- A clone from Spectral Clone has 1 health and keeps all other stats.
- A clone arrives in the same lane as the unit it copies and shares the lane with it (see Summoned Units). You may move it to another lane.
- A clone is never put into a pile or a hand when it leaves play.

## Special Tokens

A few tokens belong to particular cards. They are found in the Miscellaneous section of the Spawn menu.

- **Bell**: The River Keeper places Bell tokens on opposing units. When a unit with a Bell is destroyed, its owner discards a card from their hand or the top of their deck.
- **Resource**: a counter for Gerzite Farmer's resource tokens (spend 2 for 1 Gerzite, or 3 for 1 Dark Gerzite).
- **Life Bank**: a spectral counter for Xolox's Perish Bank. It looks like a Health counter but adds no health. It holds health that your damaged units draw from.

The Damage section of the Spawn menu also has **Direct**, **Pierce** and **Target** tokens. Put one on a unit whose target type differs from its card (for example, a Skell-Mini summoned by Navik's Beyond Death deals Direct damage).

## Turn Count

Turn count is the basic timing system that is applied to lengthened or multi-phase mechanics. A turn count is typically seen on drawn-out Spells, or as a timer for how long a Tome or Biome may be in play before being discarded.

- Some units may have a turn count timer on their special effects.
- Emperors have turn count cooldowns on their abilities.

Turn count timers may tick down at the end of your turn, or at the end of your opponent's turn. Unless specified (status effects are usually the only turn timers that have specified tick-down times), a card with a turn timer will typically tick down at the end of the controller's turn.

- If a card like a Spell or Tome is played and its effects go into play immediately, its turn timer starts decreasing at the end of the turn it is played.
- If a card with a turn timer is played but, for some reason, does not go into effect until after the turn it was played ends, its turn timer will decrease after the turn its effects go into play ends.
- Tomes have a specified turn count between when they are played and when they become active. This turn count begins to decrease after the turn the Tome was played ends.

For example, a Spell such as Fog, which changes how your opponent attacks and interacts with their side of the field, will not decrease its turn timer until the beginning of your next turn, before your Overdraw Phase. This is because the Spell's effect (For 3 turns, your opponent's Emperor, Troops, and Buildings have a 1/3 chance of missing their attacks) does not begin immediately when played; it begins after the playing field turns back over to your opponent.

= Status Effects

## Ambush

Any Troop or Building with Ambush may attack during the same turn it is played.

## Bleed

The enemy takes 1 damage per turn (before their Overdraw phase) until the effect wears off. Bleed damage stacks, but time does not.

- If less Bleed time is added to a longer Bleed time, the damage will stack until the lesser Bleed time is up.
- Buildings cannot bleed.

Example: Inflict Bleed for 3 turns (1 damage per turn, for three turns). If Inflict Bleed for 1 turn is added, the foe takes 2 damage for one turn, and then 1 damage for the remaining turns.

- If you add Inflict Bleed for 5 turns during the same turn another Inflict Bleed for 5 turns is added, the target takes 2 damage for five turns, rather than 1 damage for ten turns (no time-stacking).
- Bypasses Shield and Buffer.

Bleed turn time decreases after your Overdraw Phase.

## Buffer

Reduces incoming damage by the stated amount. Typically applied by Spells, where it will last a set number of turns.

- Buffer can also be a passive attribute on a Troop or Building, denoted by a screen symbol.

Buffer will always apply over a Shield unless specifically stated.

- Does not reduce damage from the Pierce damage attribute.

Buffer turn time (if specified) decreases during your End Phase.

## Burn

The enemy takes 1 damage per turn (before their Attack Phase) until they successfully cure Burn. Burn damage cannot stack.

- Inflicting Burn upon an already Burnt target yields an additional 1 damage for that attack.
- Buildings cannot be Burnt.
- Receiving heads on a coin flip will cure Burn.

Example: Inflict Burn will deal 1 damage to the target enemy until it is cured. It is applied before the Attacking phase to allow Spells to be played before receiving damage and to counter the effects of Burn.

- If Inflict Burn is applied to an already Burnt target, deal 1 additional damage.
- Bypasses Shield

## Comatose

Comatose enemies cannot attack until they receive any form of damage, including status damage.

- Passive abilities cannot occur while Comatose.
- Buildings and Emperors cannot become Comatose.

Activation abilities cannot be used while Comatose.

## Crumble

Crumble is a special effect that applies to Buildings only. Crumble results from targeting a building with a Comatose status effect.

- Crumbled buildings are Stunned for 1 turn and take 3 SP damage per turn for the rest of their lifespan.
- Crumble can only be cured by Divinity.
- Bypasses Buffer

## Damage Boost

An extra boost of damage denoted by the amount that lasts for the denoted number of turns. Damage Boost turn time decreases during your End Phase.

## Decay

Enemies take the denoted amount of damage (before their Attack Phase), then the effect's damage decreases by 1.

- The effect's damage is also its turn count indicator.

Example: Inflict 5 Decay will deal 5 damage to an enemy. It applies before the Attacking phase, allowing Spells to be played before receiving damage to counter Decay's effects. The next turn, the target affected by Decay takes 4 damage. This cycle continues until Decay reaches 0, or Decay is cured.

- Bypasses Shield and Buffer

## Divinity

Any card affected by Divinity is immediately cleared of all status effects, positive and negative.

- Immediate effect unless otherwise noted with a turn count.
- Reverts a Darkgale card to its normal version.

Divine units cannot be affected by any status effect while Divine.

- Does not remove attributed Thorns, Buffer, etc.

Divinity turn time (if specified) decreases during your End Phase. -

## Entrenched

Any summoned Troop or Building must wait one turn (unless specifically noted) before they may attack.

- All Troops and Buildings have one turn of Entrenched applied upon being summoned unless otherwise noted.
- Units with Entrenched are played upside down.
- Any unit with Entrenched receives 1/2 damage, rounded to the nearest whole number, from Troops, Buildings, Spells, and Emperors.
- Units with Entrenched receive any field effects upon being played, and receive full damage from status effects.

## Frenzy

The card with Frenzy may take an additional attack action for a specified number of turns.

- Multi-unit Troops and cards with split attacks may double their attack actions each.

Example: If a multi-unit Troop with 3 alive units receives Frenzy, each of the 3 units may attack twice.

- If a single Troop with 4 damage attacks in two increments of 2 damage, it may deal four increments of 2 damage.

Frenzy turn time decreases during your End Phase.

## Frozen

A Frozen target cannot attack or use any ability while frozen.

- Passive abilities are stopped as well.

Frozen units receive 1/2 incoming damage and are unfrozen after the denoted turn time is up.

- Freeze time can be stacked, but cannot stack above 3 turns.
- Buildings lose 1 SP while frozen instead of 2.
- Status effects are paused while Frozen, but are not cleared.
- Emperors can be frozen, but cannot be Frozen for more than 2 turns.
- Biomes, Tomes, and Trigger cards may be the targets of Freezing.

Example: If Inflict Freeze for 3 turns is applied to a unit that already has Inflict Freeze for 2 turns applied, the Freeze effect will be set to three turns instead of five. If an Emperor is targeted by Inflict Freeze for 3 turns, they will only become Frozen for two turns. Frozen turn time decreases during your End Phase.

## Locked

Any unit that is under Lock cannot attack or use abilities until the stated unlock price is paid in Gerzite.

- Emperors cannot be Locked.

## Overclock

Overclock adds a damage boost to a unit that decreases over time until reaching zero.

- Overclock amount is the same as the turn count.

Example: If 2 Overclock is applied to a Troop, that Troop will deal +2 damage during that turn. The next turn, it deals +1 damage, after which Overclock ends. Overclock diminishes by 1 during your End Phase.

## Overheal

Overheal adds extra Health to a Troop or Building that is currently at full health.

- Overheal diminishes by 1 over time until it reaches zero, or until the overhealed unit goes below its maximum capacity HP, at which point the effect ends.
- Overheal will apply simple healing if the unit it is applied to does not have full health. Any simple heal not used in this situation will be discarded.
- If any unit that currently has Overheal is destroyed entirely in one attack, it will survive with 1 health.

Example: If 4 Overheal is applied to a Troop with full health, that Troop will have (Full) + 4 health. For each turn the Troop stays above full health, the Overheal amount will decrease by 1.

- Buildings immediately remove all Overheal applied to them before your Overdraw phase starts, unless under any effect that negates SP decay.

Overheal diminishes by 1 immediately before your Overdraw phase starts.

## Overshield

Overshield adds a Shield to a unit that decreases over time until reaching zero.

- Overshield is one bar: more Overshield given to a unit adds to the same bar, not a new layer.
- Like a Shield, a hit takes as much off the bar as the hit was (3 damage takes off 3 Overshield). A hit as big as what is left breaks it, and the rest of that hit is lost.

- Overshield amount equals the turn count.
- Overshield applies above regular Shield.
- Overshield may end early if completely destroyed.
- Overshield cannot be bypassed by any damage or target type.
- Overshield applies to status effect damage.

Example: If 2 Overshield is applied to a Troop, that Troop will have +2 Shield during that turn. The next turn, it will have +1 Shield, after which Overshield will end.

- If Overshield is broken prematurely by damage, the effect ends.

Overshield diminishes by 1 during your End Phase.

## Possession

Any unit that is Possessed may be used by the other player for a certain number of turns.

- Possessed units retain any status buffs or debuffs.
- Possessed units attack during the attack phase of the player controlling it.
- If a Possessed unit attacks and is relieved of Possession (either through Divinity, turn count, or otherwise), it may be untapped and allowed to attack during its original controller's attack phase.
- Possessed units that can attack the enemy Emperor deal 1/2 damage, rounded to the nearest whole number.
- Emperors cannot be possessed, and neither can Minions.
- While a unit is Possessed, its owner cannot attack or tap with it.
- On the table, the Possession token's number is how many turns it has left.

If a unit with Entrenched becomes Possessed, it is immediately cured of Entrenched. Possession turn count decreases during the controller's End Phase.

## Regeneration

Regeneration provides the target with healing over a certain number of turns.

- Regeneration heals 1 health per turn at base level.
- Regeneration amount/time can stack and is not capped.
- Regeneration does not apply to Shield.
- Regeneration cannot affect Buildings.
- Regeneration heals health at the end of your turn, unless the effect says it heals at the start of your turn (click the Regeneration counter to choose).
- On the table, the Regeneration counter's number is how much it heals each turn, and its turn clock is how many turns it lasts.

Any effect with the name Heal acts as an instantaneous Regeneration effect.

- Heal applies instantly; the stated effect is executed.

Regeneration turn time decreases each time it heals (during your End Phase, or as your turn begins if it heals then).

## Stunned

The Stunned unit cannot attack for the specified number of turns.

- Tomes may be Stunned to stall one turn of effect.
- Emperors may be Stunned and cannot activate abilities while Stunned. Ability cooldowns do not halt while Stunned.

## Susceptible

Causes the marked unit to receive extra damage equal to the amount of Susceptibility.

- Susceptibility lasts until cured, or the target dies.
- Susceptibility cannot be applied to Emperors; deal damage equal to the Susceptibility amount one time if targeting an Emperor.

## Thorns (status effect)

Deals damage by the stated amount to whatever Troop, Building, or Emperor dealt damage to it. Typically an attribute, but other cards can apply it; it may last a set number of turns or until the card that applied it is discarded.

- Thorns is typically a passive attribute on a Troop or Building, denoted by a spiny symbol.

Thorns will always apply under a Shield unless specifically stated.

- Thorns will not apply damage to an enemy until any present Shield is lost.
- Thorns will not activate if the card with Thorns receives 0 damage (as a result of Buffers, Spells, Tomes, etc.)
- Does not activate if the damage dealt's source is a Spell

Thorns turn time (if specified) decreases during your End Phase.

## Weakness

A decrease in damage by the amount shown, lasting for the specified number of turns. Weakness turn time decreases during your End Phase.

